import InfoTip from '@components/charts/analytics/InfoTip'
import { formatPercent } from '@components/charts/membership/revenue/figures'
import { Card, Flex, Progress, theme, Typography } from 'antd'
import { ReactNode } from 'react'

const { Text } = Typography

interface RenewalRateGaugeProps {
  /** `null` when no renewal came due: the rate is indeterminate, not 0. */
  percent?: number | null
  /** Info icon + tooltip rendered next to the "Renewal Rate" label. */
  tooltip?: ReactNode
  /** Grey line under the value, e.g. "Card 92% · Bank transfer 71%". */
  caption?: ReactNode
}

/**
 * The 56px green ring restating the renewal rate. Extracted so the drill-down
 * page can drop it into the tile statistic's `extra` slot without duplicating
 * it.
 */
const RenewalRateRing = ({ percent }: { percent?: number | null }) => {
  const { token } = theme.useToken()

  const rate = typeof percent === 'number' ? percent : null

  return (
    <Progress
      type="circle"
      size={56}
      // An empty ring rather than a filled one when there is no rate. `format`
      // cannot make that distinction on its own: antd calls it with
      // `validProgress(percent)`, which turns null and undefined into 0.
      percent={rate ?? 0}
      strokeColor={token.colorSuccess}
      format={() => (
        <Text
          style={{
            fontSize: 12,
            fontWeight: 700,
            color: token.colorSuccess,
          }}
        >
          {formatPercent(rate, 0) ?? '--'}
        </Text>
      )}
    />
  )
}

/**
 * "Renewal Rate" card matching Figma 799-1070: label + info icon and a large
 * `NN%` value on the left, a 56px green ring restating the rate on the right.
 * Shared by the overview "Renewal & retention" block and the incoming-renewals
 * KPI cards.
 */
const RenewalRateGauge = ({
  percent,
  tooltip,
  caption,
}: RenewalRateGaugeProps) => {
  const { token } = theme.useToken()

  return (
    <Card
      // Same flex plumbing as the overview statistic, so the content stays
      // centred when a `Row align="stretch"` makes the card taller than its
      // content.
      style={{
        minHeight: 150,
        height: '100%',
        borderRadius: 8,
        display: 'flex',
        flexDirection: 'column',
      }}
      styles={{
        body: {
          padding: 20,
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <Flex
        align="center"
        justify="space-between"
        gap={12}
        style={{ flex: 1, minHeight: 110 }}
      >
        {/* Same stack as the overview statistic - 14px label, 12px gap, 28px
            value, then a reserved 15px line - so labels and values line up
            across the row even though this card has no delta to show. */}
        <Flex vertical gap={12} align="flex-start">
          <Flex align="center" gap={4}>
            <Text style={{ fontSize: 14, color: token.colorTextTertiary }}>
              Renewal rate
            </Text>
            {tooltip && (
              <InfoTip
                title={tooltip}
                style={{ color: token.colorTextTertiary, fontSize: 16 }}
              />
            )}
          </Flex>
          <Flex vertical gap={4} align="flex-start">
            <Text
              style={{
                fontSize: 28,
                fontWeight: 700,
                lineHeight: 1.2,
                color: token.colorText,
              }}
            >
              {formatPercent(percent) ?? '--'}
            </Text>
            <div style={{ minHeight: 15 }}>
              {caption && (
                <Text style={{ fontSize: 12, color: token.colorTextTertiary }}>
                  {caption}
                </Text>
              )}
            </div>
          </Flex>
        </Flex>
        <RenewalRateRing percent={percent} />
      </Flex>
    </Card>
  )
}

export default RenewalRateGauge
export { RenewalRateRing }
